import type { ListRow } from '../components/list/list-api.ts';
import type { CashDiscountSuggestion, PaymentSuggestionOptions } from './api.ts';

/**
 * Warteschlange „Braucht dich“ (Story 13.8, Entwurf R2 „Zahlungen“ Variante A · Fokus): reine Hilfen ohne Browser – wie die
 * Zahlungen aus Liste `payment_suggestions` und aus den Skonto-Vorschlägen (Story 13.7) zu einer Reihenfolge werden und
 * was „Später“ daran ändert. „Später“ merkt nur der Browser (je Person und fachlichem Tag, Muster `briefing-done.ts`);
 * geändert wird dadurch nichts im ERP. Eingeordnet hat allein `matchPayment`, die Oberfläche bucht nie von sich aus
 * (AD-16, AD-27). Abnahme durch Steuerberater/Rudolf nötig.
 */

/** Schlüssel der Spalten der Liste `payment_suggestions` (kommen vom Server, unverändert zurück). */
const COL_COUNTERPARTY = 'billing.counterparty_name'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_AMOUNT = 'billing.amount'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_BOOKING_DATE = 'billing.booking_date'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_REMITTANCE = 'billing.remittance'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_ACCOUNT = 'billing.account'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_SECTION = 'billing.section'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_CODE = 'billing.code'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_SUGGESTION_TEXT = 'billing.suggestion_text'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_DOCUMENT_NUMBERS = 'billing.document_numbers'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_ALTERNATIVES = 'billing.alternatives_count'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_REJECTED = 'billing.rejected'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_CUSTOMER_NAME = 'billing.customer_name'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_CUSTOMER_NUMBER = 'billing.customer_number'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_CURRENCY = 'billing.currency'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL
const COL_RUN_MODE = 'billing.run_mode'; // sql-lint: erlaubt Spaltenschlüssel der Liste, kein SQL

/** Spalten, die die Warteschlange aus der Liste holt. */
export const QUEUE_COLUMNS = [
  COL_COUNTERPARTY,
  COL_AMOUNT,
  COL_BOOKING_DATE,
  COL_REMITTANCE,
  COL_ACCOUNT,
  COL_SECTION,
  COL_CODE,
  COL_SUGGESTION_TEXT,
  COL_DOCUMENT_NUMBERS,
  COL_ALTERNATIVES,
  COL_REJECTED,
  COL_CUSTOMER_NAME,
  COL_CUSTOMER_NUMBER,
  COL_CURRENCY,
  COL_RUN_MODE,
] as const;

export type QueueSection = 'review' | 'none';

/** Zahlung mit Vorschlag des Rechenkerns („Bitte prüfen“) oder ohne Vorschlag, aus der Liste `payment_suggestions`. */
export interface MatchItem {
  kind: 'match';
  /** Zuordnung (Schlüssel der Listenzeile). */
  key: string;
  section: QueueSection;
  runMode: 'live' | 'probe';
  account: string;
  bookingDate: string;
  amountMinor: bigint;
  currency: string;
  counterpartyName: string | null;
  remittance: string;
  /** Grund-Code von `matchPayment` (z. B. `name_and_amount`). */
  code: string;
  suggestionText: string | null;
  documentNumbers: string | null;
  alternativesCount: number;
  rejected: boolean;
  customerName: string | null;
  customerNumber: string | null;
}

/** Skonto nach Frist (Story 13.7): eigener Inhalt im Fokus, Reihenfolge wie die übrigen. */
export interface SkontoItem {
  kind: 'skonto';
  key: string;
  section: 'review';
  suggestion: CashDiscountSuggestion;
  runMode: 'live' | 'probe';
  bookingDate: string;
}

export type QueueItem = MatchItem | SkontoItem;

function text(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Listenzeile → Zahlung; `null`, wenn die Zeile nicht lesbar ist (nie raten). */
export function matchItemFromRow(row: ListRow): MatchItem | null {
  const v = row.values;
  const money = v[COL_AMOUNT] as { amountMinor?: unknown; currency?: unknown } | null | undefined;
  if (!money || typeof money.amountMinor !== 'string' || typeof money.currency !== 'string' || !/^-?[0-9]{1,18}$/.test(money.amountMinor)) return null;
  const section = v[COL_SECTION] === 'none' ? 'none' : v[COL_SECTION] === 'review' ? 'review' : null;
  const bookingDate = text(v[COL_BOOKING_DATE])?.slice(0, 10);
  const code = text(v[COL_CODE]);
  if (!section || !bookingDate || !code) return null;
  const alternatives = Number(v[COL_ALTERNATIVES]);
  return {
    kind: 'match',
    key: row.key,
    section,
    runMode: v[COL_RUN_MODE] === 'probe' ? 'probe' : 'live',
    account: text(v[COL_ACCOUNT]) ?? '–',
    bookingDate,
    amountMinor: BigInt(money.amountMinor),
    currency: money.currency,
    counterpartyName: text(v[COL_COUNTERPARTY]),
    remittance: text(v[COL_REMITTANCE]) ?? '',
    code,
    suggestionText: text(v[COL_SUGGESTION_TEXT]),
    documentNumbers: text(v[COL_DOCUMENT_NUMBERS]),
    alternativesCount: Number.isFinite(alternatives) ? alternatives : 0,
    rejected: v[COL_REJECTED] === true,
    customerName: text(v[COL_CUSTOMER_NAME]),
    customerNumber: text(v[COL_CUSTOMER_NUMBER]),
  };
}

/**
 * Zahlung aus einer Listenzeile, die die Schlange nicht geladen hat (mehr als 200 offen): Betrag, Grund, Probe und Stand kommen
 * von den Optionen des Servers, Anzeigefelder (Datum, Konto, Auftraggeber, Verwendungszweck, Kunde) aus der Zeile, soweit die
 * Ansicht sie zeigt. Fehlt das Datum, bleibt es leer (die Anzeige zeigt „–“); geraten wird nichts.
 */
export function matchItemFromOptions(row: ListRow, o: PaymentSuggestionOptions): MatchItem {
  const v = row.values;
  const full = matchItemFromRow(row);
  return {
    kind: 'match',
    key: o.matchId,
    section: o.rejected || o.code === 'no_match' || o.code === 'customer_known' ? 'none' : 'review',
    runMode: o.runMode,
    account: full?.account ?? text(v[COL_ACCOUNT]) ?? '–',
    bookingDate: full?.bookingDate ?? text(v[COL_BOOKING_DATE])?.slice(0, 10) ?? '',
    amountMinor: BigInt(o.amountMinor),
    currency: o.currency,
    counterpartyName: text(v[COL_COUNTERPARTY]),
    remittance: text(v[COL_REMITTANCE]) ?? '',
    code: o.code,
    suggestionText: text(v[COL_SUGGESTION_TEXT]),
    documentNumbers: o.proposed.length > 0 ? o.proposed.map((x) => x.documentNumber).join(', ') : null,
    alternativesCount: o.alternatives.length,
    rejected: o.rejected,
    customerName: text(v[COL_CUSTOMER_NAME]),
    customerNumber: text(v[COL_CUSTOMER_NUMBER]),
  };
}

export function skontoItem(s: CashDiscountSuggestion): SkontoItem {
  return { kind: 'skonto', key: s.matchId, section: 'review', suggestion: s, runMode: s.runMode, bookingDate: s.bookingDate };
}

export type QueueFilter = 'all' | QueueSection;

/**
 * Reihenfolge der Warteschlange: „Bitte prüfen“ vor „Ohne Vorschlag“, darin der älteste Buchungstag zuerst (stabil, sonst
 * Reihenfolge der Eingabe); was die Person auf „Später“ gelegt hat, steht am Ende seines Bereichs in der Reihenfolge des
 * Zurückstellens. `filter` zeigt nur einen Bereich.
 */
export function orderQueue(items: readonly QueueItem[], later: readonly string[], filter: QueueFilter = 'all'): QueueItem[] {
  const rank = (i: QueueItem) => (i.section === 'review' ? 0 : 1);
  const laterIndex = (i: QueueItem) => later.indexOf(i.key);
  return items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => filter === 'all' || item.section === filter)
    .sort((a, b) => {
      const bySection = rank(a.item) - rank(b.item);
      if (bySection !== 0) return bySection;
      const la = laterIndex(a.item);
      const lb = laterIndex(b.item);
      if (la >= 0 !== lb >= 0) return la >= 0 ? 1 : -1;
      if (la >= 0 && lb >= 0) return la - lb;
      const byDate = a.item.bookingDate.localeCompare(b.item.bookingDate);
      return byDate !== 0 ? byDate : a.index - b.index;
    })
    .map(({ item }) => item);
}

/** Nächster Eintrag nach `key` (am Ende der erste); `null`, wenn die Schlange leer ist oder nur `key` enthält. */
export function neighbour(queue: readonly QueueItem[], key: string | null, step: 1 | -1): string | null {
  if (queue.length === 0) return null;
  if (queue.length === 1 && queue[0]!.key === key) return null;
  const at = key === null ? -1 : queue.findIndex((i) => i.key === key);
  if (at < 0) return queue[step === 1 ? 0 : queue.length - 1]!.key;
  return queue[(at + step + queue.length) % queue.length]!.key;
}

/** Eintrag, auf den der Fokus nach dem Erledigen von `key` springt: der nächste in der Schlange, sonst der vorige. */
export function afterRemoval(queue: readonly QueueItem[], key: string): string | null {
  const at = queue.findIndex((i) => i.key === key);
  if (at < 0) return queue[0]?.key ?? null;
  return queue[at + 1]?.key ?? queue[at - 1]?.key ?? null;
}

// ---------------------------------------------------------------------------------------------------------------
// „Später“: nur im Browser, je Person und fachlichem Tag
// ---------------------------------------------------------------------------------------------------------------

const STORAGE_PREFIX = 'till-erp.zahlung-spaeter.';
const userPrefix = (userId: string) => `${STORAGE_PREFIX}${userId}.`;
const storageKey = (userId: string, day: string) => `${userPrefix(userId)}${day}`;

/** Zurückgestellte Zuordnungen dieser Person an diesem Tag (Reihenfolge des Zurückstellens); ohne Speicher leer. */
export function readLater(userId: string, day: string): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey(userId, day));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** „Später“ merken; Stände früherer Tage derselben Person entfallen. */
export function writeLater(userId: string, day: string, keys: readonly string[]): void {
  try {
    const storage = window.localStorage;
    for (const k of Object.keys(storage)) if (k.startsWith(userPrefix(userId)) && k !== storageKey(userId, day)) storage.removeItem(k);
    if (keys.length === 0) storage.removeItem(storageKey(userId, day));
    else storage.setItem(storageKey(userId, day), JSON.stringify(keys));
  } catch {
    // Ohne Speicher gilt „Später“ bis zum Neuladen.
  }
}

/** Alles vergessen (Abmelden, Personenwechsel), siehe `clearPersonalCaches`. */
export function clearLater(): void {
  try {
    for (const k of Object.keys(window.localStorage)) if (k.startsWith(STORAGE_PREFIX)) window.localStorage.removeItem(k);
  } catch {
    // Ohne Speicher gibt es nichts zu löschen.
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Zahlen der Schlange und Passt-die-Summe-Prüfung (nur Anzeige; gebucht wird erst nach der Prüfung des Rechenkerns)
// ---------------------------------------------------------------------------------------------------------------

/**
 * Zahlen der Schlange: `decidable` = echte (Live) und entscheidbare Fälle (ein veralteter Skonto-Fall ist es nicht) – dieselbe
 * Zahl wie im Satz „n Zahlungen brauchen dich“; `probe` = Probe-Zahlungen, `stale` = veraltete Skonto-Fälle.
 */
export function queueTally(items: readonly QueueItem[]): { decidable: number; probe: number; stale: number } {
  let decidable = 0;
  let probe = 0;
  let stale = 0;
  for (const i of items) {
    if (i.runMode === 'probe') probe += 1;
    else if (i.kind === 'skonto' && i.suggestion.stale) stale += 1;
    else decidable += 1;
  }
  return { decidable, probe, stale };
}

/** Zeile „n Zahlungen brauchen dich“: nur echte, entscheidbare Fälle zählen (Probe und veraltete Skonto-Fälle nicht). */
export function queueCount(matches: readonly MatchItem[], skonto: readonly CashDiscountSuggestion[]): number {
  return queueTally([...matches, ...skonto.map(skontoItem)]).decidable;
}

/**
 * Passen die gewählten Rechnungen zur Zahlung? `ok` = Summe der offenen Beträge gleich dem Zahlungsbetrag und gleiche Währung;
 * `sum` = Summe passt nicht (Unter- oder Überzahlung klärt das ERP gesondert); `currency` = eine Rechnung hat eine andere Währung
 * (dann wird nichts summiert). Nur Anzeige: der Server bleibt die Instanz (409).
 */
export function selectionFit(options: readonly { openMinor: string; currency: string }[], paymentMinor: bigint, currency: string): 'ok' | 'sum' | 'currency' {
  if (options.some((o) => o.currency !== currency)) return 'currency';
  return options.reduce((sum, o) => sum + BigInt(o.openMinor), 0n) === paymentMinor ? 'ok' : 'sum';
}

/** Zwei Auswahlen von Posten gleich (unabhängig von der Reihenfolge)? */
export function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Bis wann „Rückgängig“ in diesem Browser gilt: aus der Restzeit des Servers (`undoRemainingMs`, unabhängig von der Uhr des
 * Browsers und der Testuhr); `undefined` = kein „Rückgängig“.
 */
export function undoUntil(remainingMs: number | null, now: number = Date.now()): Date | undefined {
  return typeof remainingMs === 'number' && remainingMs > 0 ? new Date(now + remainingMs) : undefined;
}

// ---------------------------------------------------------------------------------------------------------------
// Pick-Grenze und „gerade erledigt“
// ---------------------------------------------------------------------------------------------------------------

/**
 * Höchstzahl Rechnungen je Zahlung, nur vom Server (`maxInvoices` der Optionen, Einstellung
 * `billing_payment_match_max_invoices`). Ohne gültigen Wert `null`: Die Mehrfachwahl bleibt dann gesperrt (kein Startwert im
 * Browser, Fristen und Grenzen nur aus `settings`).
 */
export function maxInvoicesOf(options: { maxInvoices?: number } | null | undefined): number | null {
  const n = options?.maxInvoices;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

/** Die Warteschlange lädt höchstens `maxPages` Seiten; gibt es danach noch weitere, zeigt sie nur einen Teil. */
export function queueCapped(hasNextPage: boolean | undefined, pages: number, maxPages: number): boolean {
  return Boolean(hasNextPage) && pages >= maxPages;
}

/**
 * Zahlungen, die die Seite nach „Bestätigen“, „Ablehnen“ oder einer Skonto-Entscheidung ausblendet: nur so lange, bis die Listen
 * nach dem Zeitpunkt der Entscheidung neu geladen sind (`dataUpdatedAt` = der älteste Ladezeitpunkt der beteiligten Listen).
 * Danach gilt, was der Server liefert – nimmt jemand die Buchung in einem anderen Fenster zurück, steht die Zahlung wieder da.
 * `gone` = Schlüssel → Zeitpunkt (ms) der Entscheidung.
 */
export function hiddenKeys(gone: ReadonlyMap<string, number>, dataUpdatedAt: number): Set<string> {
  const hidden = new Set<string>();
  for (const [key, markedAt] of gone) if (dataUpdatedAt <= markedAt) hidden.add(key);
  return hidden;
}
